// THE FLOOR OFFLINE OUTBOX (spec 06 §9.2, D-048).
//
// A bobbin-cutting operator in a dead zone taps "Cut 200 m". The tap must
// survive: no network, a locked phone, a browser the OS killed to reclaim
// memory, a shift that ends before signal returns. And when the queue finally
// drains it must apply EXACTLY ONCE — a double-applied cut invents 200 m of
// cable that does not exist and puts it in the ledger forever.
//
// The whole design is three rules:
//
//  1. EVERY floor mutation goes through this queue, online or not. There is no
//     "if (online) fetch() else enqueue()" branch anywhere in the floor slice.
//     A code path that only runs in a dead zone is a code path that rots; the
//     office slice already proved the failure mode in v1. Online simply means
//     the queue drains in ~200 ms and the screen awaits the outcome.
//  2. The `Idempotency-Key` is a uuid v7 minted AT TAP TIME, persisted in the
//     same durable record as the body, and sent unchanged on EVERY attempt.
//     Regenerating it on a retry is how you double-apply — §9.2 exists to make
//     the replay a no-op, and it only works if the key is stable.
//  3. Nothing leaves the queue without a CONFIRMED server outcome: a 2xx, or a
//     terminal decision an operator can see and act on. Never a timeout, never
//     an optimistic delete.
//
// KILL-SAFETY, stated as the case analysis it is. Let W = the durable write of
// the record, S = the request leaving the device, R = the 2xx arriving, D = the
// delete. The engine orders them W → S → R → D and the process may die at any
// point:
//   · before W  — nothing queued, nothing sent, and the screen never said
//                 "queued". The tap is lost and the operator knows it.
//   · W..S      — replays from disk on next start. Never sent twice because it
//                 was never sent once.
//   · S..R      — replays with the SAME key. The server's UNIQUE
//                 `(tenant_id, idempotency_key)` (§9.2) returns the ORIGINAL
//                 result. Applied once.
//   · R..D      — replays with the same key; same answer; then deleted.
// So delivery is at-least-once and APPLICATION is exactly-once, which is the
// only split that survives a phone. The key and the body live in one record
// with the key as its primary key, so they can never drift apart.
//
// HEAD-OF-LINE BLOCKING IS DELIBERATE. The drain is strict FIFO and a RETRYABLE
// failure stops it rather than skipping ahead. Floor operations are ordered:
// §3.5's attribution ladder is literally "the location scan that preceded this
// unit scan", and a count line recorded before its "found extra item" line
// exists is a 404. Draining out of order would silently reorder a shift's work.
// A TERMINAL failure does not block — it moves to the needs-attention tray,
// which is exactly what the tray is for: an escape valve that keeps the queue
// moving while the operator deals with the one thing that went wrong.
//
// D-048: the TRANSPORT (queueing/replay/ordering) belongs to Module 18's sync
// protocol, which does not exist. Module 06 owns the domain idempotency and the
// conflict queue either way (§9.2), so this file keeps the policy and pushes
// the wire behind `sendFloorOp`. When 18 lands, `send` is swapped and nothing
// below this comment changes.

import { RequestError } from "../../../lib/request";
import { UnauthorizedError } from "../../../lib/problem";
import { uuidV7 } from "../../../lib/uuid";
import { CLIENT_EVENT_ID_REUSE } from "@assessly/contracts";
import {
  attachPhotoFileIds,
  dedupeKeyOf,
  deriveFollowUp,
  isSupersedable,
  opLabel,
  opTarget,
  parseFollowUp,
  resolveOutcome,
  sendFloorOp,
  type FloorFollowUp,
  type FloorOp,
  type OpLabel,
  type OpTarget,
} from "./floor-ops";
import type { PhotoUploadPort } from "./photo-upload";
import { currentFloorPrincipal, isOwnPrincipal } from "./floor-principal";
import { memoryOutboxStore, type OutboxRecord, type OutboxStore } from "./outbox-store";

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export type OutboxItemState =
  | "pending"
  | "needs_attention"
  /**
   * The server ANSWERED — 200 — and the answer said `conflicted` (D-080): the
   * event is persisted in `floor_sync_conflicts` for the office (S7) and was
   * NOT applied. Out of the send queue forever (a re-send would only dedupe
   * against the stored conflict), but never silently gone: the record stays on
   * the device as a review note until the operator explicitly dismisses it.
   * Distinct from `needs_attention`, whose items were never accepted at all
   * and may be retried.
   */
  | "needs_review";

/** What the server said when it conflicted a production event (D-080). */
export interface OutboxReview {
  /** `floor_sync_conflicts.reason`, or a synthetic client reason
   * (`result_missing` / `result_unrecognized`) when the 200 body did not
   * account for the event. */
  readonly reason: string;
  /** Epoch ms when the drain learned of the conflict (device clock). */
  readonly at: number;
}

/** What the server (or the transport) said, in the form the tray renders. */
export interface OutboxFailure {
  /** Undefined for a transport failure — no response ever arrived. */
  readonly httpStatus: number | undefined;
  /** The RFC 9457 `code` (§5's stable UPPER_SNAKE catalog). */
  readonly code: string | undefined;
  readonly message: string;
  readonly requestId: string | undefined;
  /**
   * Problem EXTENSIONS the floor acts on: `remaining_measure` + `serial_code`
   * from a 409 `INSUFFICIENT_REMAINING` (§9.1 — the fresh remaining exists so
   * the operator can RE-PLAN rather than retry blind), `available_qty_base`
   * from an `INSUFFICIENT_ATP`. A LIST extension is kept as its strings
   * joined by single spaces (`code_points` from a 422
   * DOCUMENT_GLYPH_COVERAGE), so the record stays plain strings.
   */
  readonly extensions: Readonly<Record<string, string>>;
  /** `Retry-After` in ms when the server named one (503 busy, 409 in-progress). */
  readonly retryAfterMs: number | undefined;
}

export interface OutboxItem {
  /** uuid v7, minted at tap time. Primary key AND the `Idempotency-Key`. */
  readonly clientKey: string;
  /** Monotonic FIFO position, stable across reloads. */
  readonly seq: number;
  readonly op: FloorOp;
  /** Collision identity for double-tap suppression; undefined = never deduped. */
  readonly dedupeKey: string | undefined;
  /** Deterministic hash of the op, so an identical re-tap is recognisable. */
  readonly fingerprint: string;
  readonly createdAt: number;
  readonly attempts: number;
  /** Epoch ms before which the drain will not touch this item. */
  readonly nextAttemptAt: number;
  readonly state: OutboxItemState;
  readonly lastError: OutboxFailure | undefined;
  /** Set exactly when `state === "needs_review"` (D-080). */
  readonly review: OutboxReview | undefined;
  /**
   * The PHYSICAL thing this operation is about, in the operator's own words
   * ("Bobbin 1645", "WH1/A/A-03"), captured at tap time because the screen that
   * knew it is long gone by the time the tray renders. Three rows reading
   * "Cut · 2 pieces" are not a tray, they are a puzzle.
   */
  readonly identity: string | undefined;
  /**
   * Who queued it (see floor-principal.ts). A record whose principal disagrees
   * with the signed-in one is NEVER sent — it would be attributed to the wrong
   * operator in the ledger and the audit chain.
   */
  readonly principal: string | undefined;
  /** An operation this one's RESPONSE unlocks (see floor-ops `FloorFollowUp`). */
  readonly followUp: FloorFollowUp | undefined;
  /**
   * Evidence photos this operation must ship (D-096) — ids into the durable
   * photo store beside this queue. The drain uploads them through the REAL
   * three-call flow BEFORE sending the event and binds the resulting
   * `photo_file_ids` into the payload at send time; the blobs are dropped when
   * the item leaves the queue. Absent/empty = no photos, today's normal —
   * OPTIONAL (not `| undefined`-required) so the photo-less majority of item
   * literals, including every pre-D-096 test fixture, stays valid as written.
   */
  readonly photoIds?: readonly string[] | undefined;
}

/** How an enqueued operation ended, from the calling screen's point of view. */
export type OutboxSettlement =
  | { readonly outcome: "applied"; readonly response: unknown }
  | { readonly outcome: "queued"; readonly item: OutboxItem }
  | { readonly outcome: "needs_attention"; readonly failure: OutboxFailure }
  /** The server answered 200 and REFUSED the event into `floor_sync_conflicts`
   * (D-080). Screens must show "needs review", never success. */
  | { readonly outcome: "conflicted"; readonly reason: string }
  | { readonly outcome: "superseded" };

// ---------------------------------------------------------------------------
// Pure policy (exported so the schedule is a tested table, not folklore)
// ---------------------------------------------------------------------------

/** First retry delay. Short: a dead zone in a warehouse is usually seconds. */
export const BACKOFF_BASE_MS = 5_000;

/**
 * Ceiling on the delay BETWEEN attempts. One hour, not 24: a device that
 * regains signal after a night shift must not then sit idle for another day
 * because its last backoff was a day long. The 24-hour figure in §9.2 is the
 * horizon for giving up, below — not the sleep between two tries.
 */
export const BACKOFF_MAX_DELAY_MS = 3_600_000;

/** §9.2: "retries with backoff up to 24h, then parks the entry in a tray". */
export const RETRY_HORIZON_MS = 86_400_000;

/** Exponential backoff for the Nth consecutive failure (N ≥ 1). */
export function backoffDelayMs(attempts: number): number {
  if (attempts < 1) return 0;
  const exponent = Math.min(attempts - 1, 40);
  const delay = BACKOFF_BASE_MS * 2 ** exponent;
  return Math.min(delay, BACKOFF_MAX_DELAY_MS);
}

export type FailureDisposition =
  /** Transient — try again after the backoff, keeping FIFO order. */
  | "retry"
  /** Terminal — move to the needs-attention tray now; the queue moves on. */
  | "park"
  /** The session died. Stop draining; keep everything pending and say so. */
  | "halt"
  /** The tenant is suspended (spec 15 §3.2, D-495 ruling 3). Stop draining
   * and keep everything pending, with no attempt counted: the queue waits for
   * the restore the way it waits for signal. */
  | "pause";

/**
 * Which failures are worth repeating.
 *
 * 404 is RETRYABLE on purpose, and it is the subtle one: §9.2's worked example
 * is a cut replayed before the receive that created the unit. The entity is
 * missing because the queue ahead of it has not landed yet, not because the
 * request is wrong. Parking it immediately would break the ordering guarantee
 * the queue exists to provide.
 *
 * Every other 4xx is terminal ON THE FIRST TRY. A 409 `IDEMPOTENCY_KEY_REUSE`
 * means a DIFFERENT body, or another principal (D-303), already used this key
 * — a genuine conflict, never a retry (§9.2). A 409 `INSUFFICIENT_REMAINING`
 * carries the FRESH remaining so the operator re-plans (§9.1); spinning on it
 * would burn 24 hours to reach the same answer. A 403 is a permission the
 * device does not have. A 422 is a body the server will refuse identically
 * forever.
 */
export function classifyFailure(failure: OutboxFailure, ageMs: number): FailureDisposition {
  const status = failure.httpStatus;
  if (failure.code !== undefined && SUSPENSION_CODES.has(failure.code)) return "pause";
  if (status === 401) return "halt";
  const transient =
    status === undefined ||
    status === 404 ||
    status === 408 ||
    status === 429 ||
    status >= 500 ||
    (failure.code !== undefined && RETRYABLE_CODES.has(failure.code));
  if (!transient) return "park";
  return ageMs > RETRY_HORIZON_MS ? "park" : "retry";
}

/**
 * Codes that are transient DESPITE their status.
 *
 * `IDEMPOTENCY_IN_PROGRESS` is the one that matters and the one that is easy to
 * get wrong: it is a 409, but it is the server saying "the request you sent
 * under this very key is STILL RUNNING, come back in a second" — it even ships
 * `Retry-After: 1`. That is the outbox's normal recovery path, not a conflict:
 * a request whose connection died on flaky signal, replayed under the same key
 * while the winner is still finishing a large cut. Parking it would push a
 * perfectly healthy replay into the tray and demand a human tap to finish work
 * the server was in the middle of applying.
 *
 * `RESOURCE_BUSY` is a 503 and already retryable by status; it is listed so the
 * intent survives a future status change.
 *
 * `FILE_NOT_UPLOADED` (D-096) is the photo pipeline's one retryable 422: it is
 * the registry saying "no object at the key yet" after our PUT reported 2xx —
 * a lost PUT or object-store lag, both of which a re-run of the PUT resolves.
 * The uploader resets its `uploaded` marker before rethrowing, so the next
 * attempt re-PUTs; every OTHER 422 out of the registry (integrity mismatch,
 * type refused) is terminal and parks the flag into the tray.
 */
const RETRYABLE_CODES = new Set(["IDEMPOTENCY_IN_PROGRESS", "RESOURCE_BUSY", "FILE_NOT_UPLOADED"]);

/** The tenant host refusing every write while the tenant is suspended. Not a
 * verdict on the item: it goes as it is once the tenant is restored. */
const SUSPENSION_CODES = new Set(["TENANT_SUSPENDED", "TENANT_SUSPENDED_GRACE"]);

/**
 * When the next attempt is genuinely due, defended against the device clock.
 *
 * Both `createdAt` and `nextAttemptAt` are wall-clock epochs, and a cheap
 * Android floor device boots with a wrong RTC and then NTP-corrects by hours.
 * Correct BACKWARD and `nextAttemptAt` sits far in the future — the queue stalls
 * with no tray entry and nothing to tap. So a wake that is further away than the
 * longest legal backoff cannot be real, and is treated as due now.
 */
export function dueAt(item: { attempts: number; nextAttemptAt: number }, now: number): number {
  if (item.nextAttemptAt > now + BACKOFF_MAX_DELAY_MS) return now;
  return item.nextAttemptAt;
}

/**
 * How old the queue should CONSIDER an item, for the 24-hour give-up horizon.
 *
 * `now - createdAt` is the honest answer only if the clock never moved. Correct
 * FORWARD by a day and every queued item is instantly "older than the horizon",
 * so the first transient 5xx after signal returns parks an entire shift's work
 * as "gave up" after zero real retries. The number of attempts is the one
 * quantity a clock jump cannot forge, so it caps the age: reaching the horizon
 * requires having actually failed that many times.
 */
export function effectiveAgeMs(item: { attempts: number; createdAt: number }, now: number): number {
  const wall = Math.max(0, now - item.createdAt);
  const earned = (item.attempts + 1) * BACKOFF_MAX_DELAY_MS;
  return Math.min(wall, earned);
}

/**
 * What the tray should OFFER for a parked item. Three genuinely different
 * situations, and collapsing them into one "Retry" button is how a floor app
 * teaches its users that buttons are decoration.
 */
export type ParkedAdvice =
  /** The same bytes can succeed later (aged-out 5xx / 404, a fresh grant). */
  | "retry"
  /** The world moved. Open the entity and redo the work against what is there. */
  | "replan"
  /** The server will refuse these bytes identically forever. Discard or redo. */
  | "futile";

/** Codes whose meaning is "the stock/state changed under you" (§3/§9). */
const REPLAN_CODES = new Set([
  "INSUFFICIENT_REMAINING",
  "CUT_EXCEEDS_REMAINING",
  "CUT_SERIAL_TAKEN",
  "COUNT_STATE_INVALID",
  "TRANSFER_STATE_INVALID",
  "TRANSFER_VARIANCE_REASON_REQUIRED",
  "RESERVATION_CONFLICT",
  "UNIT_NOT_AVAILABLE",
  "LOT_NOT_RELEASED",
  "NEGATIVE_STOCK_BLOCKED",
  "ADJUSTMENT_APPROVAL_REQUIRED",
  // Module 07 (spec 07 §9.1/§9.2, slice 07-A). Both are 409s, and without them
  // `parkedAdvice` falls through to its `retry` default — which for these two
  // is provably wrong: an inspection somebody else completed, or one that was
  // cancelled under a queued result, will answer identically forever. §9.1's
  // own words are "PWA shows 'Completed by {name} while you were offline' and
  // discards its queued failure-action", which is REPLAN — open the inspection,
  // look at what is there — and not a button that re-sends the same bytes.
  "QUALITY_INSPECTION_ALREADY_COMPLETED",
  "QUALITY_INSPECTION_NOT_ACTIONABLE",
  // Module 09's shipment doors (spec 09 §3.7/§9.10, P3-D PR-D3, D-286). Each
  // of these is "the stock or the document moved under the queued tap":
  // §9.10's own words for the over-pick are "the device shows a conflict
  // card; the storekeeper re-picks", which is REPLAN — open the pick screen
  // and pick again with the fresh remaining the 422 carried — and never a
  // Retry of the same bytes (a 422 would otherwise fall to `futile`, whose
  // sentence blames the operator for something the world did). CREDIT_BLOCKED
  // is deliberately NOT here: a hold placed between open and ship stops the
  // goods, the picks wait, and the same bytes DO succeed after the credit
  // decision — that is `retry`, the default.
  "QTY_EXCEEDS_REMAINING",
  "UNIT_NOT_RESERVED",
  "UNIT_NOT_SHIPPABLE",
  "UNIT_ALREADY_PICKED",
  "SHIPMENT_OPEN",
  "SHIPMENT_NOT_PICKING",
  "SHIPMENT_NOT_PACKED",
  "SHIPMENT_EMPTY",
  "SHIPMENT_TERMINAL",
  "SHIPMENT_LINE_NOT_FOUND",
  "ORDER_NOT_SHIPPABLE",
  "ORDER_LINE_TERMINAL",
  "LINE_HAS_ACTIVE_WOS",
]);

export function parkedAdvice(failure: OutboxFailure): ParkedAdvice {
  // A key bound to a different body is the one case where retry is PROVABLY
  // futile: the server compares the stored payload hash and will answer 409
  // forever (§9.2). The same 409 answers a key another principal claimed
  // (D-303). On a shared tablet that is an unstamped record drained under the
  // next operator's session after a hand-over. The first operator's request
  // with this key already reached the server, so discarding the record never
  // applies it twice. Only a request the server finished has a stored
  // outcome. That outcome is kept under the first operator, and it may have
  // been a refusal the first operator would have been told to re-plan from;
  // the next operator sees only this 409. A first request still running, or
  // one that died before it stored anything, has no outcome to keep, and its
  // effect may never have landed. The tray's discard says it undoes nothing,
  // so the operator re-enters the work if it is missing. (A claim an earlier
  // build left in progress names no principal, and answers the retryable
  // IDEMPOTENCY_IN_PROGRESS instead.)
  if (failure.code === "IDEMPOTENCY_KEY_REUSE") return "futile";
  if (failure.code !== undefined && REPLAN_CODES.has(failure.code)) return "replan";
  const status = failure.httpStatus;
  if (status === 400 || status === 422) return "futile";
  return "retry";
}

/** Deterministic JSON (sorted keys) — the double-tap fingerprint. */
export function fingerprintOp(op: FloorOp): string {
  const stable = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(stable);
    if (value !== null && typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
      return Object.fromEntries(entries.map(([k, v]) => [k, stable(v)]));
    }
    return value;
  };
  return JSON.stringify(stable(op));
}

/** Normalise any thrown value into the failure record the tray renders. */
export function describeFailure(error: unknown): OutboxFailure {
  if (error instanceof UnauthorizedError) {
    return {
      httpStatus: 401,
      code: "UNAUTHENTICATED",
      message: "",
      requestId: undefined,
      extensions: {},
      retryAfterMs: undefined,
    };
  }
  if (error instanceof RequestError) {
    const extensions: Record<string, string> = {};
    const problem = error.problem as Record<string, unknown> | undefined;
    for (const key of [
      "remaining_measure",
      "serial_code",
      "available_qty_base",
      "existing_demand_ref",
      // Module 09's shipment refusals (spec 09 §5 / the register's own
      // descriptions, P3-D PR-D3, D-286). The pick screen and the tray render
      // the code's SENTENCE from these — "Only 1200 m left on this unit",
      // "Reserved for SO-2026-00087 — not this order", "put it away first, it
      // is still in receiving" — so a key dropped here is a sentence the
      // operator never gets, and the plan's §9.10 conflict card collapses to
      // the bare server title. `remaining` is the 422's fresh figure
      // (`remaining_of` says whether it is the LINE's open quantity, 3 dp, or
      // the UNIT's measure, 6 dp); `reason` discriminates UNIT_NOT_RESERVED
      // and UNIT_NOT_SHIPPABLE; `required_role` names the lineage the 403
      // wanted; `shipment_no` names the shipment a second open collided with.
      "remaining",
      "remaining_of",
      "requested",
      "reason",
      "reserved_for_ref",
      "location_kind",
      "unit_status",
      "measure_uom",
      "uom_code",
      "required_role",
      "shipment_id",
      "shipment_no",
      "sales_order_line_id",
    ]) {
      const value = problem?.[key];
      if (typeof value === "string") extensions[key] = value;
      else if (typeof value === "number") extensions[key] = String(value);
    }
    // The delivery note's re-render (spec 09 §3.7, D-288): a 422
    // DOCUMENT_GLYPH_COVERAGE lists every character the document fonts cannot
    // print, as `"U+XXXX"` strings. The floor's sentence names them (the
    // shared `glyphCharactersSentence`), so the list is kept — joined, since
    // this record is persisted as plain strings. Anything but a list of
    // strings is dropped, and the sentence falls back to its unnamed form.
    const codePoints = problem?.["code_points"];
    if (
      Array.isArray(codePoints) &&
      codePoints.length > 0 &&
      codePoints.every((cp) => typeof cp === "string")
    ) {
      extensions["code_points"] = codePoints.join(" ");
    }
    return {
      httpStatus: error.httpStatus,
      code: error.code,
      // The SERVER's own sentence, never `RequestError.message` — that falls
      // back to `unwrap`'s internal label ("cut", "count-record"), and an
      // ALB/CloudFront 5xx returning HTML would print that token to an operator
      // as the reason their work failed.
      message: error.problem?.title ?? "",
      requestId: error.requestId,
      extensions,
      retryAfterMs: error.retryAfterMs,
    };
  }
  // No response at all: DNS/TLS/airplane mode. Transport, not the server.
  return {
    httpStatus: undefined,
    code: undefined,
    message: "",
    requestId: undefined,
    extensions: {},
    retryAfterMs: undefined,
  };
}

// ---------------------------------------------------------------------------
// Persistence mapping
// ---------------------------------------------------------------------------

function toRecord(item: OutboxItem): OutboxRecord {
  return {
    clientKey: item.clientKey,
    seq: item.seq,
    op: item.op,
    dedupeKey: item.dedupeKey ?? null,
    fingerprint: item.fingerprint,
    createdAt: item.createdAt,
    attempts: item.attempts,
    nextAttemptAt: item.nextAttemptAt,
    state: item.state,
    lastError: item.lastError ?? null,
    review: item.review ?? null,
    identity: item.identity ?? null,
    principal: item.principal ?? null,
    followUp: item.followUp ?? null,
    photoIds: item.photoIds !== undefined && item.photoIds.length > 0 ? [...item.photoIds] : null,
  };
}

/** Read a persisted photo-id list back; anything malformed is "no photos" —
 * the flag still drains (an empty `photo_file_ids` is legal on the wire), and
 * the orphaned blobs are reclaimed by the next clear(). */
function parsePhotoIds(value: unknown): readonly string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const ids = value.filter((v): v is string => typeof v === "string" && v.length > 0);
  return ids.length === value.length ? ids : undefined;
}

/** Read a persisted review note back; anything malformed is no review note. */
function parseReview(value: unknown): OutboxReview | undefined {
  if (value === null || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const reason = record["reason"];
  const at = record["at"];
  if (typeof reason !== "string" || reason.length === 0) return undefined;
  if (typeof at !== "number" || !Number.isFinite(at)) return undefined;
  return { reason, at };
}

/**
 * Read a record back, refusing anything malformed rather than crashing the
 * whole queue. A corrupt row must not take a shift's work with it — it is
 * skipped and reported, and the rows around it still drain.
 */
export function fromRecord(record: OutboxRecord): OutboxItem | undefined {
  const op = record["op"];
  const clientKey = record.clientKey;
  if (typeof clientKey !== "string" || clientKey.length === 0) return undefined;
  if (op === null || typeof op !== "object" || typeof (op as FloorOp).kind !== "string") {
    return undefined;
  }
  // An unknown state value reads as "pending" — the safe side: a re-send is
  // deduped by key (§9.2 / client_event_id), while dropping the row would lose
  // work. `needs_review` (D-080) is accepted as itself so a conflicted event
  // survives a reload as a review note, not as a fresh send.
  const state =
    record["state"] === "needs_attention"
      ? "needs_attention"
      : record["state"] === "needs_review"
        ? "needs_review"
        : "pending";
  const dedupe = record["dedupeKey"];
  const lastError = record["lastError"];
  const identity = record["identity"];
  const principal = record["principal"];
  // A row with no usable `seq` is a CORRUPT row, not a row with position zero.
  // Defaulting it to 0 would jump it to the FIFO head — a cut replayed before
  // the receive that created its unit, which is precisely the reordering the
  // strict-FIFO drain exists to prevent. Refuse it like any other corrupt row.
  if (typeof record["seq"] !== "number" || !Number.isFinite(record["seq"])) return undefined;
  return {
    clientKey,
    seq: record["seq"],
    op: op as FloorOp,
    dedupeKey: typeof dedupe === "string" ? dedupe : undefined,
    fingerprint:
      typeof record["fingerprint"] === "string"
        ? record["fingerprint"]
        : fingerprintOp(op as FloorOp),
    createdAt: typeof record["createdAt"] === "number" ? record["createdAt"] : 0,
    attempts: typeof record["attempts"] === "number" ? record["attempts"] : 0,
    nextAttemptAt: typeof record["nextAttemptAt"] === "number" ? record["nextAttemptAt"] : 0,
    state,
    lastError:
      lastError !== null && typeof lastError === "object"
        ? (lastError as OutboxFailure)
        : undefined,
    review: parseReview(record["review"]),
    identity: typeof identity === "string" && identity.length > 0 ? identity : undefined,
    principal: typeof principal === "string" && principal.length > 0 ? principal : undefined,
    followUp: parseFollowUp(record["followUp"]),
    photoIds: parsePhotoIds(record["photoIds"]),
  };
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/** Everything the chrome renders, recomputed once per change. */
export interface OutboxSnapshot {
  readonly items: readonly OutboxItem[];
  readonly pending: readonly OutboxItem[];
  readonly needsAttention: readonly OutboxItem[];
  /** Server-conflicted production events awaiting an explicit dismiss — the
   * office resolves the fact itself on S7 (D-080). */
  readonly needsReview: readonly OutboxItem[];
  /** Queued by a DIFFERENT signed-in identity — held, never sent (§9.2 fix). */
  readonly foreign: readonly OutboxItem[];
  readonly pendingCount: number;
  readonly attentionCount: number;
  readonly reviewCount: number;
  readonly foreignCount: number;
  /** True while a durable store could not be opened (queue is memory-only). */
  readonly durable: boolean;
  /**
   * True when the persisted queue could NOT be read. The honest sibling of
   * `durable: false`: there may be a shift's work on disk that this session
   * cannot see, and claiming "everything sent" would be a lie.
   */
  readonly hydrateFailed: boolean;
  /** True when the session expired mid-drain — the queue is intact, not lost. */
  readonly signedOut: boolean;
  /** Set when the FIFO head is waiting out a backoff (the drain is paused). */
  readonly blockedBy: OutboxItem | undefined;
  /**
   * True when the head has failed repeatedly with a TRANSPORT-class error while
   * the device still believes it is online — the plant-AP-with-no-backhaul case.
   * `navigator.onLine` says "associated", never "reachable", so this is the only
   * evidence the app actually has that the system cannot be reached.
   */
  readonly unreachable: boolean;
  readonly draining: boolean;
}

export interface FloorOutboxOptions {
  readonly store: OutboxStore;
  readonly send?: (op: FloorOp, idempotencyKey: string) => Promise<unknown>;
  readonly now?: () => number;
  readonly isOnline?: () => boolean;
  readonly mintKey?: (now: number) => string;
  readonly durable?: boolean;
  /** Injectable timer so the tests drive the schedule without real waiting. */
  readonly schedule?: (fn: () => void, ms: number) => () => void;
  /** Injectable identity source (see floor-principal.ts). */
  readonly principal?: () => string | undefined;
  /**
   * The evidence-photo upload port (D-096). Optional because most queues carry
   * no photos; an item WITH `photoIds` on a queue without the port parks
   * loudly rather than draining a flag whose evidence would silently vanish.
   */
  readonly photos?: PhotoUploadPort;
}

const EMPTY_SNAPSHOT: OutboxSnapshot = {
  items: [],
  pending: [],
  needsAttention: [],
  needsReview: [],
  foreign: [],
  pendingCount: 0,
  attentionCount: 0,
  reviewCount: 0,
  foreignCount: 0,
  durable: true,
  hydrateFailed: false,
  signedOut: false,
  blockedBy: undefined,
  unreachable: false,
  draining: false,
};

/** Consecutive transport failures at the head before the banner stops saying "Online". */
export const UNREACHABLE_ATTEMPTS = 2;

export class FloorOutbox {
  private readonly store: OutboxStore;
  private readonly send: (op: FloorOp, key: string) => Promise<unknown>;
  private readonly now: () => number;
  private readonly online: () => boolean;
  private readonly mint: (now: number) => string;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly principal: () => string | undefined;
  private readonly photos: PhotoUploadPort | undefined;

  private items: OutboxItem[] = [];
  private seqCounter = 0;
  private durable: boolean;
  private signedOut = false;
  private draining = false;
  private drainPromise: Promise<void> | undefined;
  private cancelWake: (() => void) | undefined;
  private hydrated = false;
  private hydratePromise: Promise<void> | undefined;
  private hydrateFailed = false;
  /** The key currently in flight, so a mid-send supersede is not resurrected. */
  private inFlight: string | undefined;

  private readonly waiters = new Map<string, (settlement: OutboxSettlement) => void>();
  private readonly listeners = new Set<() => void>();
  private cached: OutboxSnapshot = EMPTY_SNAPSHOT;
  private detach: (() => void) | undefined;

  constructor(options: FloorOutboxOptions) {
    this.store = options.store;
    this.send = options.send ?? sendFloorOp;
    this.now = options.now ?? (() => Date.now());
    this.online =
      options.isOnline ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));
    this.mint = options.mintKey ?? uuidV7;
    this.principal = options.principal ?? currentFloorPrincipal;
    this.photos = options.photos;
    this.durable = options.durable ?? true;
    this.schedule =
      options.schedule ??
      ((fn, ms) => {
        const handle = setTimeout(fn, ms);
        return () => {
          clearTimeout(handle);
        };
      });
    this.recompute();
  }

  // -- subscription --------------------------------------------------------

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Stable object identity between changes — safe for useSyncExternalStore. */
  snapshot(): OutboxSnapshot {
    return this.cached;
  }

  private recompute(): void {
    const sorted = [...this.items].sort(
      (a, b) => a.seq - b.seq || (a.clientKey < b.clientKey ? -1 : 1),
    );
    const current = this.principal();
    const own = sorted.filter((i) => isOwnPrincipal(i.principal, current));
    const foreign = sorted.filter((i) => !isOwnPrincipal(i.principal, current));
    const pending = own.filter((i) => i.state === "pending");
    const attention = own.filter((i) => i.state === "needs_attention");
    const review = own.filter((i) => i.state === "needs_review");
    const head = pending[0];
    const blocked = head !== undefined && head.attempts > 0 ? head : undefined;
    this.cached = {
      items: sorted,
      pending,
      needsAttention: attention,
      needsReview: review,
      foreign,
      pendingCount: pending.length,
      attentionCount: attention.length,
      reviewCount: review.length,
      foreignCount: foreign.length,
      durable: this.durable,
      hydrateFailed: this.hydrateFailed,
      signedOut: this.signedOut,
      blockedBy: blocked,
      unreachable:
        blocked !== undefined &&
        blocked.attempts >= UNREACHABLE_ATTEMPTS &&
        blocked.lastError?.httpStatus === undefined,
      draining: this.draining,
    };
  }

  private emit(): void {
    this.recompute();
    for (const listener of this.listeners) listener();
  }

  // -- lifecycle -----------------------------------------------------------

  /**
   * Load the persisted queue. Idempotent; safe to call from a React effect.
   *
   * NEVER REJECTS, and — the part that is easy to get wrong — never LATCHES on
   * failure. Setting `hydrated` before awaiting the read would mean one
   * transient `InvalidStateError` (a connection the browser closed under memory
   * pressure) permanently convinces the session that the queue is empty: the
   * banner reads "Everything sent" while a shift of cuts sits on disk, and the
   * seq counter restarts at 0 so new items collide with the invisible ones.
   * Instead the failure is recorded, surfaced (`hydrateFailed`) and RETRIED on
   * the next call.
   */
  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    if (this.hydratePromise !== undefined) return this.hydratePromise;
    const pass = (async () => {
      let records: OutboxRecord[];
      try {
        records = await this.store.readAll();
      } catch {
        this.hydrateFailed = true;
        this.emit();
        return;
      }
      const items: OutboxItem[] = [];
      for (const record of records) {
        const item = fromRecord(record);
        if (item !== undefined) items.push(item);
      }
      this.items = items;
      this.seqCounter = items.reduce((max, item) => Math.max(max, item.seq), 0);
      this.hydrated = true;
      this.hydrateFailed = false;
      this.emit();
    })().finally(() => {
      this.hydratePromise = undefined;
    });
    this.hydratePromise = pass;
    return pass;
  }

  /**
   * Attach the connectivity/focus triggers and start draining. §9.2's "replay
   * on reconnect" plus the practical one nobody writes down: a phone in a
   * pocket fires no `online` event until it is woken, so the visibility change
   * when the operator looks at the screen is the trigger that actually works.
   */
  start(target: Window | undefined = typeof window === "undefined" ? undefined : window): void {
    void this.hydrate()
      .then(() => this.drain())
      .catch(() => {
        /* hydrate never rejects; drain never rejects. Belt and braces. */
      });
    if (target === undefined || this.detach !== undefined) return;
    const wake = (): void => {
      void this.drain();
    };
    // Regaining signal is NEW INFORMATION that invalidates the backoff. Without
    // this an operator who walks out of a dead zone into solid wifi watches
    // "Online · 14 waiting to send" for up to an hour, with the items pending
    // (so the tray is empty) and nothing to tap. Backoff protects the server
    // from a device that keeps failing — not from a device whose network just
    // came back.
    const onOnline = (): void => {
      void this.sendNow();
    };
    const onVisible = (): void => {
      if (typeof document === "undefined" || document.visibilityState === "visible") wake();
    };
    target.addEventListener("online", onOnline);
    target.addEventListener("focus", wake);
    target.addEventListener("visibilitychange", onVisible);
    this.detach = () => {
      target.removeEventListener("online", onOnline);
      target.removeEventListener("focus", wake);
      target.removeEventListener("visibilitychange", onVisible);
    };
  }

  stop(): void {
    this.detach?.();
    this.detach = undefined;
    this.cancelWake?.();
    this.cancelWake = undefined;
  }

  // -- enqueue -------------------------------------------------------------

  /**
   * Queue one operation. THE KEY IS MINTED HERE — at tap time — and this method
   * resolves only once the record is durable, so a caller that has been told
   * "queued" is telling the operator the truth.
   */
  async enqueue(
    op: FloorOp,
    meta: {
      identity?: string | undefined;
      followUp?: FloorFollowUp | undefined;
      /** Durable photo-store ids this op must ship (D-096). The caller persists
       * the blobs FIRST, then enqueues — blobs-before-flag, so a death between
       * the two leaves an orphaned blob, never a flag pointing at nothing. */
      photoIds?: readonly string[] | undefined;
    } = {},
  ): Promise<OutboxItem> {
    await this.hydrate();
    const fingerprint = fingerprintOp(op);
    const dedupeKey = dedupeKeyOf(op);

    let superseded: OutboxItem | undefined;
    if (dedupeKey !== undefined) {
      const existing = this.items.find(
        (item) => item.dedupeKey === dedupeKey && item.state === "pending",
      );
      if (existing !== undefined) {
        // Exact double-tap (bounced finger, scanner double-fire): one operation.
        if (existing.fingerprint === fingerprint) return existing;
        // A CORRECTION of a last-write-wins operation (§5 row 40). The pending
        // row is dropped and a fresh one appended — with a NEW key, because a
        // changed body under the old key is exactly the 409 IDEMPOTENCY_KEY_REUSE
        // §9.2 defines. The old key is retired, never reused.
        if (isSupersedable(op)) superseded = existing;
      }
    }

    this.seqCounter += 1;
    const item: OutboxItem = {
      clientKey: this.mint(this.now()),
      seq: this.seqCounter,
      op,
      dedupeKey,
      fingerprint,
      createdAt: this.now(),
      attempts: 0,
      nextAttemptAt: 0,
      state: "pending",
      lastError: undefined,
      review: undefined,
      identity: meta.identity,
      principal: this.principal(),
      followUp: meta.followUp,
      photoIds: meta.photoIds !== undefined && meta.photoIds.length > 0 ? meta.photoIds : undefined,
    };
    // W — durable BEFORE anything is sent. The kill-safety case analysis in the
    // header depends on this single `await` being here and not later.
    //
    // And the REPLACEMENT is written before the superseded row is removed, not
    // after. A kill in that window then leaves a transient duplicate of a
    // last-write-wins operation (harmless: the server takes the last one), where
    // the other order would lose the CORRECTION and replay the typo.
    await this.store.put(toRecord(item));
    this.items.push(item);
    if (superseded !== undefined) {
      await this.store.remove(superseded.clientKey);
      this.items = this.items.filter((i) => i.clientKey !== superseded.clientKey);
      this.settle(superseded.clientKey, { outcome: "superseded" });
    }
    this.emit();
    void this.drain();
    return item;
  }

  /**
   * Enqueue and, when there is a network, wait for the server's answer so the
   * screen can show the real result (a cut's children, a 409's fresh
   * remaining). Offline — or if the queue is still busy after `timeoutMs` — it
   * resolves `queued`, which is a complete and honest outcome, not a failure.
   */
  async enqueueAndWait(
    op: FloorOp,
    timeoutMs = 15_000,
    meta: {
      identity?: string | undefined;
      followUp?: FloorFollowUp | undefined;
      photoIds?: readonly string[] | undefined;
    } = {},
  ): Promise<OutboxSettlement> {
    const item = await this.enqueue(op, meta);
    if (!this.online()) return { outcome: "queued", item };
    return new Promise<OutboxSettlement>((resolve) => {
      let done = false;
      const finish = (settlement: OutboxSettlement): void => {
        if (done) return;
        done = true;
        cancel();
        resolve(settlement);
      };
      const cancel = this.schedule(() => {
        const current = this.items.find((i) => i.clientKey === item.clientKey);
        finish({ outcome: "queued", item: current ?? item });
      }, timeoutMs);
      // A duplicate re-tap collapses onto the SAME key (see enqueue), so two
      // callers can be waiting on one item. Chaining rather than overwriting is
      // what makes the first caller's promise settle on the real outcome
      // instead of sitting out its 15-second timeout while the work succeeds.
      const previous = this.waiters.get(item.clientKey);
      this.waiters.set(item.clientKey, (settlement) => {
        previous?.(settlement);
        finish(settlement);
      });
    });
  }

  private settle(clientKey: string, settlement: OutboxSettlement): void {
    const waiter = this.waiters.get(clientKey);
    if (waiter !== undefined) {
      this.waiters.delete(clientKey);
      waiter(settlement);
    }
  }

  // -- tray actions --------------------------------------------------------

  /** Move a parked item back into the queue — with its ORIGINAL key. */
  async retry(clientKey: string): Promise<void> {
    const item = this.items.find((i) => i.clientKey === clientKey);
    if (item === undefined) return;
    // A review row is NOT retryable (D-080): the server already persisted the
    // event into `floor_sync_conflicts`, so a re-send under the same key would
    // only dedupe against the stored conflict. The office resolves the fact
    // itself (S7); the device only dismisses the note.
    if (item.state === "needs_review") return;
    const revived: OutboxItem = {
      ...item,
      state: "pending",
      nextAttemptAt: 0,
      // `attempts` is NOT reset: the count is the item's history, and resetting
      // it would restart the 24-hour horizon from a button press.
    };
    await this.store.put(toRecord(revived));
    this.replace(revived);
    this.signedOut = false;
    this.emit();
    void this.drain();
  }

  /**
   * Throw a parked item away. The caller MUST have confirmed this with the
   * operator, and the confirmation must say what the tray says: discarding
   * removes the queued attempt, it does not undo anything the server already
   * applied — a 409 conflict means someone else's version won, and that version
   * stays.
   */
  async discard(clientKey: string): Promise<void> {
    const item = this.items.find((i) => i.clientKey === clientKey);
    await this.store.remove(clientKey);
    this.items = this.items.filter((i) => i.clientKey !== clientKey);
    // A discarded flag takes its queued evidence with it (D-096): the operator
    // confirmed the attempt is abandoned, and a blob no record references is
    // unreachable weight on a cheap tablet.
    if (item?.photoIds !== undefined) void this.photos?.discard(item.photoIds);
    this.emit();
  }

  /** Device hand-over / sign-out: drop everything — queued blobs included. */
  async clear(): Promise<void> {
    const photoIds = this.items.flatMap((item) => item.photoIds ?? []);
    await this.store.clear();
    this.items = [];
    if (photoIds.length > 0) void this.photos?.discard(photoIds);
    this.emit();
  }

  /**
   * "Send now" — the operator's override.
   *
   * Clears the pending backoff and drains immediately. Exposed as a button and
   * fired on the `online` transition, because both are the same statement: the
   * network situation changed, so the wait the queue computed from the last
   * failure is stale. It does NOT touch `attempts` (the 24-hour horizon is a
   * property of the item's history, not of how many times a human tapped) and
   * it does not revive parked items — those are the tray's business.
   */
  async sendNow(): Promise<void> {
    await this.hydrate();
    const cleared = this.cached.pending.filter((item) => item.nextAttemptAt !== 0);
    for (const item of cleared) {
      const revived: OutboxItem = { ...item, nextAttemptAt: 0 };
      try {
        await this.store.put(toRecord(revived));
      } catch {
        // A store failure here is not a reason to refuse to try sending.
      }
      this.replace(revived);
    }
    if (cleared.length > 0) this.emit();
    await this.drain();
  }

  private replace(next: OutboxItem): void {
    this.items = this.items.map((item) => (item.clientKey === next.clientKey ? next : item));
  }

  // -- drain ---------------------------------------------------------------

  /**
   * Drain the queue. Concurrent calls join the in-flight pass.
   *
   * NEVER REJECTS. It is called as `void this.drain()` from enqueue, from the
   * connectivity listeners and from the backoff timer, so a rejection would be
   * an unhandled promise on a device nobody is watching. A storage failure is a
   * reason to STOP this pass and try again later, not to tear down the queue —
   * and stopping leaves the item exactly where it was, which is the safe side
   * of the exactly-once split (the replay is deduped by key, §9.2).
   */
  drain(): Promise<void> {
    if (this.drainPromise !== undefined) return this.drainPromise;
    const promise = this.runDrain()
      .catch(() => {
        // A store failure. The item is still on disk; the next trigger retries.
        this.wakeAt(BACKOFF_BASE_MS);
      })
      .finally(() => {
        this.drainPromise = undefined;
        this.draining = false;
        this.emit();
      });
    this.drainPromise = promise;
    return promise;
  }

  private async runDrain(): Promise<void> {
    await this.hydrate();
    this.draining = true;
    this.emit();

    for (;;) {
      if (this.signedOut) return;
      if (!this.online()) return;
      const head = this.cached.pending[0];
      if (head === undefined) return;

      const now = this.now();
      const due = dueAt(head, now);
      if (due > now) {
        this.wakeAt(due - now);
        return;
      }

      try {
        // PHOTOS FIRST (D-096, the spec-18 §3.4 gate landed early): a flag's
        // evidence blobs go through the REAL three-call upload BEFORE the event
        // is sent, and the resulting `photo_file_ids` are bound into the
        // payload under the flag's own tap-time key — §2.11's "the outbox
        // enqueues the upload first and derives these ids from its response".
        // The upload is resumable from the durable photo records, so a death
        // anywhere in here replays with the SAME ids. A failure throws into
        // the ordinary classifier below: transient → backoff (head-of-line,
        // deliberately — the flag must not drain without its evidence),
        // terminal or aged-out → the needs-attention tray, loudly.
        let opToSend = head.op;
        if (head.photoIds !== undefined && head.photoIds.length > 0) {
          if (this.photos === undefined) {
            // No uploader wired on a queue holding a photo-carrying flag: a
            // configuration defect, parked loudly rather than draining a flag
            // whose evidence would silently vanish.
            throw new RequestError(
              {
                type: "about:blank",
                status: 422,
                title: "This queue cannot upload evidence photos",
                code: "PHOTO_PORT_MISSING",
              },
              422,
              "photo upload port missing",
            );
          }
          const fileIds = await this.photos.ensureUploaded(head.photoIds);
          opToSend = attachPhotoFileIds(head.op, fileIds);
        }
        // S — the same key every attempt. Never re-minted.
        this.inFlight = head.clientKey;
        const response = await this.send(opToSend, head.clientKey);
        this.inFlight = undefined;
        // INTERPRET BEFORE DELETING (D-080). Endpoint 13 answers 200 with a
        // PER-EVENT status, so a 2xx is NOT yet "applied": a `conflicted`
        // event deleted here would settle as success and the operator's
        // rejected report would vanish under a green tick — the silent-loss
        // class TESTING §5.4's third leg exists to catch. Module-06 kinds
        // resolve `applied` unconditionally (their 2xx IS the application).
        const resolution = resolveOutcome(head.op, head.clientKey, response);
        if (resolution.kind === "conflicted" && resolution.reason === CLIENT_EVENT_ID_REUSE) {
          // D-340 ruling 3: the server answered that this key is already
          // stored for ANOTHER worker, and wrote nothing. There is no row on S7
          // for the office to resolve, so a review note would wait forever on
          // a resolution nobody can make. The tap is parked as a failure the
          // tray reads as futile, the same advice the interceptor's own
          // IDEMPOTENCY_KEY_REUSE gets: re-sending the same key cannot work,
          // and the operator re-enters the tap if it still matters.
          if (!this.items.some((i) => i.clientKey === head.clientKey)) {
            this.emit();
            continue;
          }
          const failure: OutboxFailure = {
            httpStatus: 409,
            code: "IDEMPOTENCY_KEY_REUSE",
            message:
              "This tap's key is already used by another worker, so the server applied nothing.",
            requestId: undefined,
            extensions: { conflict_reason: CLIENT_EVENT_ID_REUSE },
            retryAfterMs: undefined,
          };
          const parked: OutboxItem = {
            ...head,
            attempts: head.attempts + 1,
            state: "needs_attention",
            lastError: failure,
          };
          await this.store.put(toRecord(parked));
          this.replace(parked);
          this.settle(head.clientKey, { outcome: "needs_attention", failure });
          this.emit();
          continue;
        }
        if (resolution.kind === "conflicted") {
          // The item may have been discarded/cleared while in flight — same
          // resurrection hazard as the catch path below: writing the review
          // record back would revive a row the operator removed.
          if (!this.items.some((i) => i.clientKey === head.clientKey)) {
            this.emit();
            continue;
          }
          // Out of the SEND queue forever (the server persisted the event
          // into `floor_sync_conflicts`; a re-send would only dedupe), but
          // never silently gone: the record TRANSITIONS to `needs_review`,
          // keeping its tap-time identity, with the server's reason and when
          // we learned it. The office resolves the fact itself (S7); the
          // tray's only affordance is an explicit confirmed dismiss.
          const reviewed: OutboxItem = {
            ...head,
            state: "needs_review",
            review: { reason: resolution.reason, at: this.now() },
            lastError: undefined,
          };
          await this.store.put(toRecord(reviewed));
          this.replace(reviewed);
          this.settle(head.clientKey, { outcome: "conflicted", reason: resolution.reason });
          // The event — WITH its uploaded photo ids — is persisted server-side
          // in `floor_sync_conflicts`, so the device-side blobs have done their
          // job (D-096). Dropping them here is what keeps a conflicted flag
          // from pinning megabytes to a review note.
          if (head.photoIds !== undefined) void this.photos?.discard(head.photoIds);
          this.emit();
          // The queue is not blocked — drain the next item. A follower that
          // depended on this event (a qty report's op_completed) will conflict
          // server-side in turn (§3.2's guard) and land here too: the FIFO
          // cascade is intended behaviour, honest and visible (D-080).
          continue;
        }
        // R..D — the delete happens ONLY after a confirmed applied/deduplicated
        // outcome. `deduplicated` settles as applied: it is the replay of a
        // tap the server already applied once (§9.2 / client_event_id UNIQUE),
        // which is this queue's normal recovery, not a new fact.
        await this.store.remove(head.clientKey);
        this.items = this.items.filter((item) => item.clientKey !== head.clientKey);
        this.settle(head.clientKey, { outcome: "applied", response });
        // The flag applied with its `photo_file_ids` — the evidence lives in
        // the registry now, and the device-side blobs are dropped (D-096).
        if (head.photoIds !== undefined) void this.photos?.discard(head.photoIds);
        this.emit();
        // The chained operation (§4.5's found item) is queued DURABLY here, not
        // handed to a screen that may not be mounted. It gets its own tap-time
        // key and its own place at the back of the FIFO.
        if (head.followUp !== undefined) {
          const next = deriveFollowUp(head.followUp, response);
          if (next !== undefined) {
            await this.enqueue(next, { identity: head.identity });
          }
        }
        continue;
      } catch (error) {
        this.inFlight = undefined;
        // The item may have been SUPERSEDED, discarded or cleared while its
        // request was in flight — `head` is a captured local, not a live row.
        // Writing the outcome back unconditionally would resurrect a corrected
        // count line on disk (invisible in memory, replayed after a restart,
        // overwriting the correction). If it is gone, its outcome is moot.
        if (!this.items.some((i) => i.clientKey === head.clientKey)) {
          this.emit();
          continue;
        }
        const failure = describeFailure(error);
        const disposition = classifyFailure(failure, effectiveAgeMs(head, this.now()));

        if (disposition === "pause") {
          // Kept exactly as it was, attempts and all: the item is fine, the
          // tenant is not taking writes. The drain gate (`isOnline`) holds the
          // queue until the suspension is over.
          const held: OutboxItem = { ...head, lastError: failure };
          await this.store.put(toRecord(held));
          this.replace(held);
          this.emit();
          return;
        }

        if (disposition === "halt") {
          // The session expired. Nothing is lost and nothing is retried: the
          // item stays pending with its key, and the chrome asks for a sign-in.
          this.signedOut = true;
          const held: OutboxItem = { ...head, lastError: failure };
          await this.store.put(toRecord(held));
          this.replace(held);
          this.emit();
          return;
        }

        const attempts = head.attempts + 1;
        if (disposition === "park") {
          const parked: OutboxItem = {
            ...head,
            attempts,
            state: "needs_attention",
            lastError: failure,
          };
          await this.store.put(toRecord(parked));
          this.replace(parked);
          this.settle(head.clientKey, { outcome: "needs_attention", failure });
          this.emit();
          // The tray took it out of the queue's way — keep draining.
          continue;
        }

        // The server's own `Retry-After` beats the local schedule when it is
        // SHORTER: `IDEMPOTENCY_IN_PROGRESS` means "one second", and answering
        // that with a five-minute exponential wait is the client inventing a
        // delay nobody asked for.
        const advised = failure.retryAfterMs;
        const backoff = backoffDelayMs(attempts);
        const delay = advised !== undefined ? Math.min(advised, backoff) : backoff;
        const retried: OutboxItem = {
          ...head,
          attempts,
          nextAttemptAt: this.now() + delay,
          lastError: failure,
        };
        await this.store.put(toRecord(retried));
        this.replace(retried);
        this.emit();
        this.wakeAt(delay);
        // FIFO: a transient failure at the head STOPS the pass (see header).
        return;
      }
    }
  }

  private wakeAt(delayMs: number): void {
    this.cancelWake?.();
    this.cancelWake = this.schedule(
      () => {
        this.cancelWake = undefined;
        void this.drain();
      },
      Math.max(0, delayMs),
    );
  }

  /**
   * Report whether the queue is genuinely on disk.
   *
   * Opening IndexedDB is asynchronous, so the engine is constructed with
   * `durable: false` and corrected here once the answer is known. Pessimistic
   * first is the only safe order: "your queue is not durable" shown for 40 ms
   * and withdrawn costs nothing, while the reverse would tell an operator their
   * work was safe before anyone had checked.
   */
  setDurable(durable: boolean): void {
    if (this.durable === durable) return;
    this.durable = durable;
    this.emit();
  }

  /** Clear the signed-out latch after a fresh sign-in. */
  resumeAfterSignIn(): void {
    this.signedOut = false;
    this.emit();
    void this.drain();
  }
}

// ---------------------------------------------------------------------------
// Presentation helpers (pure, shared by the chrome and the tray)
// ---------------------------------------------------------------------------

export function itemLabel(item: OutboxItem): OpLabel {
  return opLabel(item.op);
}

export function itemTarget(item: OutboxItem): OpTarget {
  return opTarget(item.op);
}

/** A queue with no durable store, for tests and for the IndexedDB-less fallback. */
export function createMemoryOutbox(options: Omit<FloorOutboxOptions, "store"> = {}): FloorOutbox {
  return new FloorOutbox({ ...options, store: memoryOutboxStore(), durable: false });
}
